import { useFormContext } from 'react-hook-form';

import type { FormSelectProps } from '../types';
import type { FieldValues } from 'react-hook-form';

import { Select } from '@/components/Select';
import { Field, FieldDescription, FieldError } from '@/components/ui/field';
import { FormField } from '@/components/ui/form';

export const EMPTY_SELECT_VALUE = '__none__';

export const FormSelect = <TFieldValues extends FieldValues = FieldValues>(
  props: FormSelectProps<TFieldValues>,
) => {
  const {
    name,
    label,
    description,
    isRequired,
    options,
    disabled,
    searchable,
    control,
    onChange,
    selectProps,
  } = props;
  const formContext = useFormContext<TFieldValues>();
  const formControl = control || formContext.control;

  const fieldId = `form-field-${String(name)}`;

  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field, fieldState }) => {
        const value = field.value ? String(field.value) : EMPTY_SELECT_VALUE;
        const fieldLabel = (
          <>
            {label}
            {isRequired && (
              <span
                className={
                  fieldState.invalid
                    ? 'text-destructive'
                    : 'text-muted-foreground'
                }
              >
                *
              </span>
            )}
          </>
        );

        return (
          <Field data-invalid={fieldState.invalid}>
            <Select
              id={fieldId}
              value={value}
              onValueChange={(newValue) => {
                const actualValue =
                  newValue === EMPTY_SELECT_VALUE ? null : newValue || null;
                field.onChange(actualValue);
                if (actualValue !== null) {
                  onChange?.(actualValue);
                }
              }}
              options={options}
              label={fieldLabel}
              searchPlaceholder={label ? `${label}...` : undefined}
              disabled={disabled}
              searchable={searchable}
              isInvalid={fieldState.invalid}
              aria-invalid={fieldState.invalid}
              {...selectProps}
            />
            {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            {description && <FieldDescription>{description}</FieldDescription>}
          </Field>
        );
      }}
    />
  );
};
